import { WithID } from './base';
import { Listing } from './listing';
import { Profile, ProfileUserType } from './profile';
import { PopStoreExtendedUserAccessLevel } from './creator-subscription';
import { AppDate } from '../envTypes';

export type ClipAuditLog = {
  message?: string;
  userId: string;
  approverName?: string;
  timestamp: string;
  status?: 'Waiting' | 'Approved' | 'Rejected';
};

export type ClipModerationInfo = {
  aiFlagUnderReview?: boolean;
  isContentModerated?: boolean;
  lastReviewedByAi?: boolean;
  aiVerdict?: 'Passed' | 'Failed' | 'Requires Human Intervention';
};

export type PopClipProductInfo = WithID<Listing> & {
  productId?: string;
  shopifyProductId?: string;
  shopifyProductHandle?: string;
  shopifyProductUrl?: string;
};

export enum PopClipStatus {
  Uploading = 'uploading',
  Uploaded = 'uploaded',
  Processed = 'processed',
  Failed = 'failed',
}

export type VideoClipSourceType = 'instagram' | 'tiktok';

export type TranscriptAttachmentInfo = {
  filename: string;
  size: number;
  type: string;
  url: string;
};

export type MediaGalleryObjectTag =
  | 'com.popstore.course.video'
  | 'com.popstore.clips.video'
  | 'com.popstore.post.image'
  | 'com.popstore.post.video'
  | 'com.popshoplive.clips.video'
  | 'com.popstore.button.video'
  | 'com.popstore.button.image'
  | 'com.popstore.group_message.image'
  | 'com.popstore.group_message.video';

export interface PopClipMetadata {
  auditLogs?: ClipAuditLog[];
  available?: boolean;
  featured?: boolean;
  gifURL?: string;
  sellerId: string;
  profile: WithID<Profile>;
  userType?: ProfileUserType;
  avatar?: string;
  createdAt: string;
  updatedAt?: string;
  videoTitle?: string;
  videoURL: string;
  processedURL?: string;
  shopifyDomain?: string;
  videoDescription?: string;
  thumbnailURL?: string;
  videoCoverUrl?: string;
  product?: PopClipProductInfo;
  products?: PopClipProductInfo[];
  status?: PopClipStatus;
  isPrivate?: boolean;
  accessLevel?: PopStoreExtendedUserAccessLevel;
  showLockedVideoThumbnail?: boolean;
  importedFrom?: VideoClipSourceType;
  externalVideoUrl?: string;
  contentType?: string;
  isStartOfChapter?: boolean;
  previewDuration?: string;
  customThumbnailImageURL?: string;
  customThumbnailMetaidentifier?: string;
  defaultThumbnailImageURL?: string;
  defaultThumbnailImageStatus?: 'processing' | 'processed';
  muteForUnsubscribedUsers?: boolean;
  transcriptAttachment?: TranscriptAttachmentInfo;
  tag?: MediaGalleryObjectTag;
  /** type of items being tagged to the video */
  taggedItemType?: 'products' | 'calendar' | 'external-links';
  /** external links (for external links tagging) */
  taggedExternalLinks?: Array<{
    url: string;
    linkText: string;
    linkSubtext?: string;
    linkThumbnailUrl?: string;
    unfurled?: boolean;
  }>;
}

export enum ClipFileType {
  Clips = 'clips',
  ShopifyClips = 'shopifyClips',
  PopStoreClips = 'popStoreClips',
  PopStorePosts = 'popStorePosts',
  csClips = 'csClips',
}

export type PopClipItemWithId = ClipModerationInfo & {
  id: string;
  metaIdentifier: string;
  sortKey: string;
  createdAt: string;
  updatedAt?: string;
  isExternal?: boolean;
  fileType: ClipFileType;
  cdnUrl: string;
  status?: PopClipStatus;
  metaData: PopClipMetadata;
};

// /popClips/{sellerId}-{metaIdentifier}
export interface PopClip {
  metaIdentifier: string;
  sellerId: string;
  fileType: 'clips' | 'shopifyClips' | 'popStoreClips' | 'popStorePosts';
  cdnUrl: string;
  metaData: any & {
    // moderation info
    auditLogs: ClipAuditLog[] | undefined;
  }
  status?: 'processed' | 'uploaded' | string;
  lastRefreshedAt: AppDate;
  isCopied?: boolean;
  isContentModerated?: boolean;
  origin: {
    updatedAt: string;
    createdAt?: string;
  };
  createdAt?: AppDate;
  upvoteCount?: number;
}

export interface PopClipShopifyProduct {
  defaultImage: string;
  shopifyProductId: string;
  shopifyProductUrl: string;
  shopifyProductHandle: string;
}
